import { createColumnHelper } from '@tanstack/react-table'
import type { ParseKeys } from 'i18next'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useSearchParams } from 'react-router-dom'

import { byDecimal } from '@/components/data/byDecimal'
import { ColumnsMenu } from '@/components/data/ColumnsMenu'
import {
  DateRangeControl,
  isDateRange,
  type DateRange,
} from '@/components/data/DateRangeControl'
import { FacetFilter } from '@/components/data/FacetFilter'
import { RecordCard, RecordList } from '@/components/data/RecordList'
import { TableEmptyState } from '@/components/data/TableEmptyState'
import {
  toHiddenColumns,
  toVisibilityState,
  useViewState,
} from '@/components/data/useViewState'
import { EmptyState } from '@/components/feedback/EmptyState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { toOptions } from '@/components/ui/control'
import { Sheet } from '@/components/ui/Sheet'
import { StatusPill } from '@/components/ui/StatusPill'
import { TabNav } from '@/components/ui/TabNav'
import { TabPanel } from '@/components/ui/TabPanel'
import { Tag } from '@/components/ui/Tag'
import { cn } from '@/lib/cn'
import { formatDate, formatMoney, formatNumber, formatTimestamp } from '@/lib/format'
import {
  useAging,
  useApprovals,
  useBalances,
  useDecideApproval,
  usePayments,
  useRecordPayment,
  toPaymentPayload,
} from '@/features/finance/api/useFinance'
import { PaymentFormSheet } from '@/features/finance/components/FinanceFormSheets'
// Still `InvoicesPanel.tsx` on disk, and the export inside it is no longer
// called that: the tab lists a case's invoices AND its debit notes now, so a
// component named for one of the two would misdescribe half of what it renders.
// Moving the file is a rename this pass deliberately did not make while other
// work was in flight in the same tree; the next reader should do it.
import { DocumentsPanel } from '@/features/finance/components/InvoicesPanel'
import { PaymentAllocations } from '@/features/finance/components/PaymentAllocations'
import { PaymentStatusTag } from '@/features/finance/components/PaymentStatusTag'
import type { ApprovalRequest, CounterpartyBalance, Payment } from '@/types/domain'

/* Money. The page subtitle is "Who owes whom", because that is the actual
 * question — say "Money", not "Financials".
 *
 * Every surface here shares ONE date-range control (FR-53), and this page no
 * longer declares its own set of periods: the vocabulary and the control are
 * `components/data/DateRangeControl`, which the dashboard mounts too. A second
 * declaration here is exactly how this page and the dashboard came to filter
 * by different periods while looking alike.
 *
 * ── THE DENSE-TABLE LAYER, AND THE TWO TABS THAT ARE NOT TABLES ─────────────
 *
 * Story 6.5's criterion is that these views behave "as the standard dense
 * table" (UX-DR-4): sortable by any column, filters and column choices
 * persisted per surface, row click opening the record and never expanding in
 * place, virtualised above 100 rows. This page had NONE of it and the quotes
 * and cases lists had all of it, because they were built by different passes —
 * so what was needed was to adopt the existing layer (`RecordList` over
 * `DataTable`, `useViewState`, `ColumnsMenu`, `FacetFilter`), not to write a
 * second one.
 *
 * Three tabs took it: BALANCES, PAYMENTS and the paper in DOCUMENTS. Each is
 * a list of like rows, which is what a dense table is for, and the payments tab
 * had an in-place disclosure — precisely the shape the criterion names as the
 * thing not to do — so its allocations moved into a sheet.
 *
 * TWO DID NOT, and refusing was the point rather than an omission:
 *
 *   · AGING is a five-bar summary, not a list of rows. That was true of the
 *     SHAPE and is no longer true of the DATA: `GET /finance/aging` now answers
 *     `{items, rows}`, and `rows` is the per-item `AgedItem` list this file used
 *     to say no endpoint served. What is still absent is the drill-through UI —
 *     a bucket you can click into, a table under it — and that is a screen to
 *     design rather than a field to rename, so it is owned separately and this
 *     panel goes on rendering the five bars only. The claim that has to stay
 *     dead is the old one: the rows exist, and the next reader should not be
 *     told to go and add the query.
 *   · APPROVALS is a decision queue. UX-DR-20 requires the requested change
 *     shown ALONGSIDE the current value together with who is deciding, plus the
 *     reason in full and two actions — which is a card, and a dense row cannot
 *     hold it without dropping the thing the criterion asks for. Story 6.5's
 *     "rate changes" VIEW is a different surface: every entry regardless of
 *     outcome, filterable, read-only (`RateChangeHistoryEntry`, declared in
 *     `types/domain.ts` and consumed by nothing, because no endpoint serves it).
 *     That one is a table when it exists; this one is not.
 *
 * NEITHER of those two is a fixture any more: `/finance/aging` and
 * `/finance/balances` are answered by `internal/finance` over real rows, and the
 * `internal/showcase` fixtures behind them are deleted. The wire moved with
 * them — `AgingBucket.key` became `id`, `CounterpartyBalance.entityId` stopped
 * existing, `kind` lost three of its four values — and every place this page
 * still read the fixture's spelling was reading `undefined` off the live server
 * rather than failing loudly. See `types/domain.ts`, which is the authority on
 * what actually arrives.
 *
 * THE GAP IN THE SHARED LAYER THAT USED TO BE HERE IS CLOSED, and this note
 * survives only to stop the workaround being copied: `RecordList` did not
 * forward `DataTable`'s `filtered` + `onClearFilters`, so the UX-DR-17 split had
 * to be hand-built as an empty NODE that reached both halves. It forwards both
 * now, and the Documents panel passes the props. The two panels below still
 * pass nodes — they work, and rewriting a working empty state was not this
 * pass's business — but a NEW surface should pass the props. */

const TABS = [
  { key: 'balances', labelKey: 'finance.tabs.balances' },
  { key: 'aging', labelKey: 'finance.tabs.aging' },
  { key: 'payments', labelKey: 'finance.tabs.payments' },
  { key: 'approvals', labelKey: 'finance.tabs.approvals' },
  { key: 'documents', labelKey: 'finance.tabs.documents' },
] as const

type TabKey = (typeof TABS)[number]['key']

const balanceColumn = createColumnHelper<CounterpartyBalance>()

const BALANCE_COLUMN_MENU: readonly {
  id: string
  labelKey: ParseKeys
  locked?: boolean
}[] = [
  { id: 'name', labelKey: 'finance.balances.counterparty', locked: true },
  { id: 'kind', labelKey: 'common.fields.type' },
  { id: 'receivable', labelKey: 'finance.balances.receivable' },
  { id: 'payable', labelKey: 'finance.balances.payable' },
  { id: 'net', labelKey: 'finance.balances.net' },
  { id: 'oldestDays', labelKey: 'finance.balances.oldest' },
]

const BALANCE_LOCKED: readonly string[] = BALANCE_COLUMN_MENU.filter(
  (entry) => entry.locked,
).map((entry) => entry.id)

/* TWO kinds, not four. Finance keys money under `client` or `participant`
   because that is the only counterparty TYPE its own tables record, and it may
   not learn the directory's vocabulary to add up money (AD-2).
   `agent`, `counter-agent` and `carrier` were the FIXTURE's words: the live read
   has never sent one, so while they sat here the facet offered three options
   that matched no row and emptied the table on click. What sort of participant a
   party is remains the directory's question, not this report's. */
const BALANCE_KINDS: readonly CounterpartyBalance['kind'][] = ['client', 'participant']

/* Per-counterparty balances (FR-35).
 *
 * ONE view state for the whole tab, and N tables under it — one per issuing
 * entity, because FR-35 breaks the position down per entity and netting across
 * two of them is the merge the breakdown exists to prevent. A `useViewState` per
 * entity is not available even in principle: the entities arrive as data and a
 * hook cannot be called in a loop over them. Sharing one is also the better
 * answer — "sort by net descending" is a habit about the report, not about
 * Ashgabat, and it should hold on every card at once.
 *
 * WHAT IS STILL MISSING, and it is the server's to send: FR-35 asks for a NETTED
 * ROLLUP per entity and there is none here. It is deliberately not computed on
 * this side — summing `Number(row.net.amount)` across the rows is money
 * arithmetic in the client, which AD-9 puts server-side and in decimal, and a
 * rollup that disagreed with the ledger by a cent would be worse than an absent
 * one.
 */
function BalancesPanel({ range }: { range: DateRange }) {
  const { t } = useTranslation()
  const { data, isPending, isError, error, refetch } = useBalances(range)
  const [viewState, controls] = useViewState('finance.balances')

  const kindFilter = viewState.filters.kind ?? NO_FILTER

  const rows = useMemo(() => {
    const all = data ?? []
    if (kindFilter.length === 0) return all
    return all.filter((row) => kindFilter.includes(row.kind))
  }, [data, kindFilter])

  const columnVisibility = useMemo(
    () =>
      toVisibilityState(
        viewState.hiddenColumns.filter((id) => !BALANCE_LOCKED.includes(id)),
      ),
    [viewState.hiddenColumns],
  )

  const columns = useMemo(
    () => [
      balanceColumn.accessor('name', {
        header: t('finance.balances.counterparty'),
        cell: (info) => (
          <span className="text-text-heading">{info.getValue()}</span>
        ),
      }),
      balanceColumn.accessor('kind', {
        header: t('common.fields.type'),
        size: 132,
        cell: (info) => <Tag>{info.getValue()}</Tag>,
      }),
      balanceColumn.accessor((row) => row.receivable.amount, {
        id: 'receivable',
        header: t('finance.balances.receivable'),
        size: 128,
        meta: { numeric: true },
        sortingFn: byDecimal<CounterpartyBalance>((row) => row.receivable.amount),
        cell: (info) => formatNumber(info.getValue(), 2),
      }),
      balanceColumn.accessor((row) => row.payable.amount, {
        id: 'payable',
        header: t('finance.balances.payable'),
        size: 128,
        meta: { numeric: true },
        sortingFn: byDecimal<CounterpartyBalance>((row) => row.payable.amount),
        cell: (info) => formatNumber(info.getValue(), 2),
      }),
      balanceColumn.accessor((row) => row.net.amount, {
        id: 'net',
        header: t('finance.balances.net'),
        size: 128,
        meta: { numeric: true },
        sortingFn: byDecimal<CounterpartyBalance>((row) => row.net.amount),
        cell: (info) => (
          <span
            className={cn(
              'font-semibold',
              Number(info.getValue()) < 0 ? 'text-negative' : 'text-positive',
            )}
          >
            {formatNumber(info.getValue(), 2)}
          </span>
        ),
      }),
      balanceColumn.accessor('oldestDays', {
        header: t('finance.balances.oldest'),
        size: 132,
        meta: { numeric: true },
        /* ALWAYS PRESENT and SIGNED. The absent case this cell used to branch on
           was the fixture's; the live read sends a number on every row, so the
           `undefined` arm was dead code guarding a state that can no longer
           occur.
           NEGATIVE is the fact that replaces it: the party's earliest obligation
           has not fallen due yet, which is the least urgent row on the card and
           the exact distinction the `not-yet-due` bucket draws. So the redness
           test reads the SIGNED number — an `Math.abs` added to tidy the column
           would paint -72 the same red as 72 and put the one counterparty nobody
           needs to chase at the top of it — and the label is the aging bucket's
           own words rather than a second phrasing of the same state, because two
           strings for one fact is how two screens come to disagree in one
           language and not the other. The magnitude is dropped with it: "not due
           for 72 days" is a countdown nobody chases by, and the bar chart on the
           next tab is where not-yet-due money is actually sized. */
        cell: (info) => {
          const days = info.getValue()
          return (
            <span className={cn(days > 60 ? 'text-negative' : 'text-text-muted')}>
              {days < 0
                ? t('finance.aging.buckets.not-yet-due')
                : t('finance.balances.oldestDays', { count: days })}
            </span>
          )
        },
      }),
    ],
    [t],
  )

  if (isPending) return <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  /* Keyed on the entity NAME, because that is all the wire carries: the real
     read serves `entityName` and no `entityId`, so the display text is the only
     grouping key available. This is a genuine weakness rather than a preference
     and it is worth naming here — two of our legal entities renamed to read
     alike fold into one card whose figures net across both, and nothing in the
     payload can tell them apart. Keying on the id it used to key on was worse
     than weak: no such field arrives, so every row grouped under `undefined` and
     the tab rendered ONE table netting every entity together, which is precisely
     the merge FR-35's per-entity breakdown exists to prevent.

     THE BLANK ENTITY is a decision, not a fallthrough. The server sends '' when
     a party's documents span more than one of our entities — deliberately blank
     rather than the first one found, because naming one of two would be a claim
     the data does not support — so those rows belong under no entity heading.
     They are collected into their own card, titled with the em-dash the rest of
     the product uses for a value that is not there, and sorted last so the named
     entities lead the report. The alternative, letting '' key a card like any
     other, puts an untitled card among titled ones and invites the reader to
     take it for an entity whose name failed to load; the worse alternative,
     folding those rows into a named entity's card, would show a net figure
     that silently mixed Ashgabat's receivable with Yiwu's payable. */
  const byEntity = rows.reduce<Record<string, CounterpartyBalance[]>>((acc, row) => {
    ;(acc[row.entityName] ??= []).push(row)
    return acc
  }, {})

  // `sort` is stable, so this moves the blank group to the end and leaves every
  // named entity in the order the rows arrived in.
  const entityCards = Object.entries(byEntity).sort(([a], [b]) =>
    a === '' ? 1 : b === '' ? -1 : 0,
  )

  const controlsRow = (
    <span className="flex flex-wrap items-center gap-2">
      <FacetFilter
        label={t('common.fields.type')}
        options={toOptions(BALANCE_KINDS, (kind) => kind)}
        selected={kindFilter}
        onChange={(values) => controls.setFilter('kind', values)}
      />
      <ColumnsMenu
        columns={BALANCE_COLUMN_MENU.map((entry) => ({
          id: entry.id,
          label: t(entry.labelKey),
          locked: entry.locked,
        }))}
        hidden={viewState.hiddenColumns}
        onToggle={controls.toggleColumn}
        onReset={() => controls.setHiddenColumns([])}
      />
    </span>
  )

  /* Emptiness is decided ONCE, here, rather than per entity card: with the facet
     narrowing everything away there are no cards at all, so a per-card empty
     state would never render and the reader would be left with a blank tab. */
  if (rows.length === 0) {
    return (
      <Card>
        <CardHeader action={controlsRow}>
          <CardTitle>{t('finance.tabs.balances')}</CardTitle>
        </CardHeader>
        <TableEmptyState
          filtered={kindFilter.length > 0 && (data ?? []).length > 0}
          onClearFilters={controls.clearFilters}
          title={t('finance.balances.emptyTitle')}
          description={t('finance.balances.emptyDescription')}
        />
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-3.5">
      {entityCards.map(([entityName, entityRows], index) => {
        /* The em-dash is the product's placeholder for a value that is not
           there, and "our entity" genuinely is not there on these rows — see
           the grouping above. Resolved once and used for the heading, the
           table's accessible name and the phone card alike, so the three cannot
           come to say different things about the same group. */
        const entityLabel = entityName === '' ? t('common.empty') : entityName
        return (
        <Card key={entityName}>
          {/* The controls sit on the FIRST card only. They govern the tab, not
              one entity, and repeating them per card would read as four
              independent filters over four independent tables. */}
          <CardHeader action={index === 0 ? controlsRow : undefined}>
            <CardTitle>{entityLabel}</CardTitle>
          </CardHeader>

          <RecordList
            label={t('finance.balances.tableLabel', { entity: entityLabel })}
            data={entityRows}
            columns={columns}
            getRowId={(row) => row.id}
            renderCard={(row) => (
              <RecordCard
                id={row.name}
                status={<Tag>{row.kind}</Tag>}
                primary={entityLabel}
                secondary={
                  row.oldestDays < 0
                    ? t('finance.aging.buckets.not-yet-due')
                    : t('finance.balances.oldestDays', { count: row.oldestDays })
                }
                value={formatNumber(row.net.amount, 2)}
                valueLabel={t('finance.balances.net')}
              />
            )}
            /* No `onRowClick`: FR-35's drill-through is to the counterparty's
               cases, and there is no per-counterparty case list to open. The
               row's `id` could not open one either — it is an opaque `kind:uuid`
               handle whose uuid is usually derived from the party's NAME, so
               taking it to the directory returns a 404; `directoryId` is the
               field a link would have to use, and it is '' for a party no
               document ever linked. A row that looked clickable and did nothing
               would be worse than one that does not. */
            sorting={viewState.sorting}
            onSortingChange={controls.setSorting}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={(next) =>
              controls.setHiddenColumns(toHiddenColumns(next))
            }
          />
        </Card>
        )
      })}
    </div>
  )
}

function AgingPanel({ range }: { range: DateRange }) {
  const { t } = useTranslation()
  const { data, isPending, isError, error, refetch } = useAging(range)

  if (isPending) return <Skeleton className="h-48 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  /* Emptiness here is "nothing is outstanding", NOT an empty array. The server
     always emits all five buckets, the empty ones included — an aged bucket
     that holds nothing is a fact worth showing, not a row to omit — so a book
     with nothing on it arrives as five zero rows. Reading the length would
     leave the founder staring at five empty bars, and the *nothing yet* state
     it is supposed to reach would never fire. */
  const outstanding = data.some((bucket) => Number(bucket.amount.amount) !== 0)

  if (!outstanding) {
    return (
      <Card>
        <EmptyState
          title={t('finance.aging.emptyTitle')}
          description={t('finance.aging.emptyDescription')}
        />
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('finance.aging.title')}</CardTitle>
      </CardHeader>
      <ul className="flex flex-col px-[18px] pb-3">
        {data.map((bucket, index) => (
          <li
            key={bucket.id}
            className={cn(
              'flex items-center gap-3 py-3',
              index !== data.length - 1 && 'border-line-hair border-b',
            )}
          >
            {/* Coloured from `overdue`, never from a list of which keys are
                red: the server decides what "late" means, and a client that
                kept its own list would disagree with it the first time a
                bucket was added. */}
            <span
              aria-hidden
              className="size-2 shrink-0 rounded-full"
              style={{
                background: bucket.overdue
                  ? 'var(--color-negative)'
                  : 'var(--color-blue-500)',
              }}
            />
            {/* The label is translated from the bucket's id. A server-sent label
                would arrive in one language and print in the other — which is
                why the wire carries no label at all.
                `id`, not `key`: the fixture spelled this field `key` and the
                real read spells it `id`, so every one of these five bars was
                building `finance.aging.buckets.undefined` and printing the raw
                key back at the reader. The TOKENS did not move, so the
                catalogue's five entries are untouched. */}
            <span className="text-text-body w-36 text-[13px]">
              {t(`finance.aging.buckets.${bucket.id}`)}
            </span>

            <span className="bg-neutral-100 h-2 flex-1 overflow-hidden rounded-full">
              <span
                className="block h-full rounded-full"
                style={{
                  width: `${bucket.sharePercent}%`,
                  background: bucket.overdue
                    ? 'var(--color-negative)'
                    : 'var(--color-blue-500)',
                }}
              />
            </span>

            <span
              className={cn(
                'asa-num w-32 text-right text-[13px]',
                bucket.overdue ? 'text-negative font-semibold' : 'text-text-heading',
              )}
            >
              {formatMoney(bucket.amount.amount, bucket.amount.currency)}
            </span>
            <span className="asa-num text-text-muted w-10 text-right text-[12px]">
              {bucket.sharePercent}%
            </span>
          </li>
        ))}
      </ul>
    </Card>
  )
}

/* The payment log (Story 4.5).
 *
 * Two figures per row rather than one, because a payment has two states worth
 * distinguishing and only one of them used to be shown: what has been applied
 * to paper and what is still sitting there unapplied. Unallocated cash is
 * legal, is never auto-applied (AD-11), and is exactly what AC 4 requires to be
 * VISIBLE as unallocated — so it is coloured, weighted and given its own
 * column, and a fully applied payment goes quiet by comparison.
 *
 * A row opens onto its allocations rather than onto an edit sheet. There is no
 * amend-a-payment route in this story, and what a reader actually wants from a
 * payment row is what it settled.
 */
const paymentColumn = createColumnHelper<Payment>()

const PAYMENT_COLUMN_MENU: readonly {
  id: string
  labelKey: ParseKeys
  locked?: boolean
}[] = [
  { id: 'reference', labelKey: 'finance.payments.reference', locked: true },
  { id: 'counterpartyName', labelKey: 'finance.balances.counterparty' },
  { id: 'receivedOn', labelKey: 'finance.payments.received' },
  { id: 'amount', labelKey: 'common.fields.amount' },
  { id: 'allocated', labelKey: 'finance.payments.allocated' },
  { id: 'unallocated', labelKey: 'finance.payments.unallocated' },
  { id: 'status', labelKey: 'finance.payments.status' },
]

const PAYMENT_LOCKED: readonly string[] = PAYMENT_COLUMN_MENU.filter(
  (entry) => entry.locked,
).map((entry) => entry.id)

const PAYMENT_DIRECTIONS: readonly Payment['direction'][] = ['in', 'out']

/** "Nothing selected", shared so the memo below does not recompute per paint. */
const NO_FILTER: readonly string[] = []

function PaymentsPanel({ range }: { range: DateRange }) {
  const { t } = useTranslation()
  const { data, isPending, isError, error, refetch } = usePayments(range)
  const [recording, setRecording] = useState(false)
  /** The one payment whose allocations are open, as a SHEET rather than an
   *  in-place expansion: UX-DR-4 is explicit that a row click opens the record
   *  and never expands in place, and the disclosure this replaces was the
   *  criterion's own counter-example sitting on the money page. */
  const [openPaymentId, setOpenPaymentId] = useState<string | null>(null)
  const record = useRecordPayment()
  const [viewState, controls] = useViewState('finance.payments')
  /* «Ждут подтверждения», held in local state rather than in the persisted view
     state beside the direction facet: it is a WORKING queue somebody empties in
     one sitting, not a way of looking at the log they want to find still on
     next week. */
  const [pendingOnly, setPendingOnly] = useState(false)

  const directionFilter = viewState.filters.direction ?? NO_FILTER

  /* How many records are still waiting on Finance (0059). Counted over the
     LOADED rows — the register already pages by date range, and a status query
     parameter is a server change to make when volume asks for it rather than
     ahead of it. Before the direction facet, so narrowing to outgoing payments
     does not quietly change what the counter claims about the period. */
  const awaiting = useMemo(
    () => (data ?? []).filter((payment) => payment.status === 'pending').length,
    [data],
  )

  /* Applied CLIENT-SIDE over rows the server already scoped. That direction is
     the whole rule (AD-5, AD-21): a facet here can only narrow a set the scope
     predicate fixed, and nothing it holds is forwarded as a query parameter. */
  const rows = useMemo(() => {
    const all = data ?? []
    const byStatus =
      pendingOnly && awaiting > 0
        ? all.filter((payment) => payment.status === 'pending')
        : all
    if (directionFilter.length === 0) return byStatus
    return byStatus.filter((payment) => directionFilter.includes(payment.direction))
  }, [data, directionFilter, pendingOnly, awaiting])

  const columnVisibility = useMemo(
    () =>
      toVisibilityState(
        viewState.hiddenColumns.filter((id) => !PAYMENT_LOCKED.includes(id)),
      ),
    [viewState.hiddenColumns],
  )

  const columns = useMemo(
    () => [
      paymentColumn.accessor('reference', {
        header: t('finance.payments.reference'),
        size: 196,
        cell: (info) => (
          <span className="flex items-center gap-2">
            <span className="text-text-heading font-mono text-[12.5px]">
              {info.getValue()}
            </span>
            <Tag tone={info.row.original.direction === 'in' ? 'brand' : 'neutral'}>
              {t(`finance.payments.${info.row.original.direction}`)}
            </Tag>
          </span>
        ),
      }),
      paymentColumn.accessor('counterpartyName', {
        header: t('finance.balances.counterparty'),
        cell: (info) => info.getValue(),
      }),
      paymentColumn.accessor('receivedOn', {
        header: t('finance.payments.received'),
        size: 116,
        cell: (info) => formatDate(info.getValue()),
      }),
      paymentColumn.accessor((row) => row.amount.amount, {
        id: 'amount',
        header: t('common.fields.amount'),
        size: 132,
        meta: { numeric: true },
        sortingFn: byDecimal<Payment>((row) => row.amount.amount),
        cell: (info) => (
          <>
            {formatNumber(info.getValue(), 2)}
            <span className="text-text-muted ml-1 text-[11.5px] font-normal">
              {info.row.original.amount.currency}
            </span>
          </>
        ),
      }),
      paymentColumn.accessor((row) => row.allocated.amount, {
        id: 'allocated',
        header: t('finance.payments.allocated'),
        size: 124,
        meta: { numeric: true },
        sortingFn: byDecimal<Payment>((row) => row.allocated.amount),
        cell: (info) => formatNumber(info.getValue(), 2),
      }),
      paymentColumn.accessor((row) => row.unallocated.amount, {
        id: 'unallocated',
        header: t('finance.payments.unallocated'),
        size: 132,
        meta: { numeric: true },
        sortingFn: byDecimal<Payment>((row) => row.unallocated.amount),
        /* Unallocated cash is legal and visible; it is never auto-applied
           (AD-11). Showing it — and weighting it — is the point. */
        cell: (info) => (
          <span
            className={cn(
              Number(info.getValue()) > 0
                ? 'text-attention font-semibold'
                : 'text-text-muted',
            )}
          >
            {formatNumber(info.getValue(), 2)}
          </span>
        ),
      }),
      /* The record's status, in its long form here and as a badge on the row's
         reference. It qualifies NOTHING in the columns beside it: a pending
         payment's amount, allocated and unallocated figures are the same
         figures a confirmed one's are (spec L4). */
      paymentColumn.accessor('status', {
        id: 'status',
        header: t('finance.payments.status'),
        size: 152,
        cell: (info) =>
          info.getValue() === 'pending' ? (
            <Tag tone="attention">{t('finance.payments.statuses.pending')}</Tag>
          ) : (
            <span className="text-text-muted">
              {t('finance.payments.statuses.confirmed')}
            </span>
          ),
      }),
    ],
    [t],
  )

  if (isPending) return <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  /* Read from the LIST rather than held in state, so the sheet shows the row the
     server most recently confirmed. Allocating invalidates the payment queries,
     the list refetches, and the open sheet's figures move with it — held in
     state it would keep showing the balance as it was before the allocation it
     just made. */
  const openPayment = rows.find((payment) => payment.id === openPaymentId)

  return (
    <Card>
      <CardHeader
        action={
          <span className="flex flex-wrap items-center gap-2">
            <FacetFilter
              label={t('finance.payments.directionLabel')}
              options={toOptions(PAYMENT_DIRECTIONS, (direction) =>
                t(`finance.payments.${direction}`),
              )}
              selected={directionFilter}
              onChange={(values) => controls.setFilter('direction', values)}
            />
            {/* The queue, offered only when there IS one: a chip counting zero
                on every desk with nothing outstanding is noise, and the number
                is the whole reason the chip is worth a place in the header. */}
            {awaiting > 0 ? (
              <Button
                variant={pendingOnly ? 'primary' : 'secondary'}
                size="sm"
                aria-pressed={pendingOnly}
                onClick={() => setPendingOnly(!pendingOnly)}
              >
                {t('finance.payments.pendingFilter', { count: awaiting })}
              </Button>
            ) : null}
            <ColumnsMenu
              columns={PAYMENT_COLUMN_MENU.map((entry) => ({
                id: entry.id,
                label: t(entry.labelKey),
                locked: entry.locked,
              }))}
              hidden={viewState.hiddenColumns}
              onToggle={controls.toggleColumn}
              onReset={() => controls.setHiddenColumns([])}
            />
            <Button variant="secondary" size="sm" onClick={() => setRecording(true)}>
              {t('finance.payments.record')}
            </Button>
          </span>
        }
      >
        <CardTitle>{t('finance.payments.title')}</CardTitle>
      </CardHeader>

      <RecordList
        label={t('finance.payments.title')}
        data={rows}
        columns={columns}
        getRowId={(payment) => payment.id}
        onRowClick={(payment) => setOpenPaymentId(payment.id)}
        renderCard={(payment) => (
          <RecordCard
            id={payment.reference}
            status={
              <span className="flex items-center gap-1.5">
                <Tag tone={payment.direction === 'in' ? 'brand' : 'neutral'}>
                  {t(`finance.payments.${payment.direction}`)}
                </Tag>
                <PaymentStatusTag status={payment.status} />
              </span>
            }
            primary={payment.counterpartyName}
            secondary={formatDate(payment.receivedOn)}
            value={formatNumber(payment.unallocated.amount, 2)}
            valueLabel={t('finance.payments.unallocated')}
          />
        )}
        sorting={viewState.sorting}
        onSortingChange={controls.setSorting}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={(next) =>
          controls.setHiddenColumns(toHiddenColumns(next))
        }
        /* The two facts UX-DR-17 keeps apart, and the second condition is what
           keeps them apart: a facet over an already-empty log narrowed nothing,
           so a first-day Finance user meets "no payments recorded" rather than
           "nothing matches" and a Clear button that reveals an empty list. It
           reads the FACET only — the date range has its own control and no "all"
           option, so offering to clear it would offer a button that cannot do
           what it says.

           Passed as a NODE rather than through DataTable's own `filtered` prop,
           which `RecordList` does not forward — see the note at the top of this
           file. The node reaches both halves, so the phone card list makes the
           same distinction the table does. */
        empty={
          (directionFilter.length > 0 || pendingOnly) && (data ?? []).length > 0 ? (
            <TableEmptyState
              filtered
              onClearFilters={() => {
                controls.clearFilters()
                setPendingOnly(false)
              }}
            />
          ) : (
            <EmptyState
              title={t('finance.payments.emptyTitle')}
              description={t('finance.payments.emptyDescription')}
            />
          )
        }
      />

      {/* Closed from onSuccess, so a refusal leaves the sheet open with the
          draft intact rather than closing over a save that never happened. */}
      <PaymentFormSheet
        open={recording}
        onClose={() => setRecording(false)}
        pending={record.isPending}
        submitError={record.isError ? record.error.message : undefined}
        onSubmit={(draft) =>
          record.mutate(toPaymentPayload(draft), {
            onSuccess: () => setRecording(false),
          })
        }
      />

      {/* Gated on the ROW rather than on the id, like the approval sheet: a row
          that vanished from the list under a refetch has no figures to render,
          and a sheet open over `undefined` is how a screen shows a payment that
          is no longer there. */}
      {openPayment ? (
        <Sheet
          open
          onClose={() => setOpenPaymentId(null)}
          title={t('finance.payments.detailTitle', {
            reference: openPayment.reference,
          })}
        >
          {/* The facts AC 1 requires a payment to carry and the scannable row
              cannot hold: how the money moved, which of OUR entities it moved
              through, and the booking the server took at the case's basis
              (AD-9). The USD figure is read, never recomputed here. */}
          <div className="text-text-muted flex flex-wrap gap-x-5 gap-y-1 text-[12.5px] font-light">
            <span>{t(`finance.payments.methods.${openPayment.method}`)}</span>
            <span>{openPayment.issuingEntityName}</span>
            <span className="asa-num">
              {t('finance.payments.bookedAt', {
                rate: openPayment.appliedRate,
                date: formatDate(openPayment.rateDate),
                usd: formatNumber(openPayment.usd, 2),
              })}
            </span>
          </div>

          <PaymentAllocations payment={openPayment} />
        </Sheet>
      ) : null}
    </Card>
  )
}

function ApprovalsPanel() {
  const { t } = useTranslation()
  const { data, isPending, isError, error, refetch } = useApprovals()
  const decide = useDecideApproval()
  /* The request being confirmed for approval. Rejection acts directly: a
     rejected request can be re-raised, an approved rate is binding. */
  const [confirming, setConfirming] = useState<ApprovalRequest | null>(null)

  if (isPending) return <Skeleton className="h-48 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  if (data.length === 0) {
    return (
      <Card>
        <EmptyState
          title={t('finance.approvals.emptyTitle')}
          description={t('finance.approvals.emptyDescription')}
        />
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-3.5">
      {data.map((approval) => {
        const raise = Number(approval.delta.amount) > 0
        return (
          <Card key={approval.id}>
            <div className="flex flex-col gap-3 p-[18px]">
              <div className="flex flex-wrap items-center gap-2">
                <NavLink
                  to={`/cases/${approval.caseId}`}
                  className="text-brand text-[13px] font-semibold"
                >
                  {approval.caseNumber}
                </NavLink>
                <StatusPill tone="attention">{t('finance.approvals.pending')}</StatusPill>
                <span className="text-text-muted text-[12.5px] font-light">
                  {approval.requesterName} · {formatTimestamp(approval.requestedAt)}
                </span>
              </div>

              {/* The requested change alongside the current value, so the
                  requester is never left guessing whether it was seen. */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <span className="flex flex-col gap-1">
                  <span className="asa-eyebrow">{t('finance.approvals.current')}</span>
                  <span className="asa-num text-text-body text-[15px]">
                    {formatMoney(
                      approval.currentRate.amount,
                      approval.currentRate.currency,
                    )}
                  </span>
                </span>
                <span className="text-text-faint" aria-hidden>
                  →
                </span>
                <span className="flex flex-col gap-1">
                  <span className="asa-eyebrow">{t('finance.approvals.requested')}</span>
                  <span className="asa-num text-text-heading text-[15px] font-semibold">
                    {formatMoney(
                      approval.requestedRate.amount,
                      approval.requestedRate.currency,
                    )}
                  </span>
                </span>
                <span className="flex flex-col gap-1">
                  <span className="asa-eyebrow">{t('finance.approvals.change')}</span>
                  <span
                    className={cn(
                      'asa-num text-[15px] font-semibold',
                      raise ? 'text-positive' : 'text-negative',
                    )}
                  >
                    {formatMoney(approval.delta.amount, approval.delta.currency)}
                  </span>
                </span>
              </div>

              <p className="text-text-body border-line-hair border-t pt-2.5 text-[13px] leading-snug font-light">
                {approval.reason}
              </p>

              <div className="flex gap-2">
                {/* Approving a rate change asks first: it is financially
                    binding, so it confirms rather than offering undo. */}
                <Button
                  size="sm"
                  onClick={() => setConfirming(approval)}
                  disabled={decide.isPending}
                >
                  {t('finance.approvals.approve')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    decide.mutate({
                      id: approval.id,
                      caseId: approval.caseId,
                      decision: 'reject',
                    })
                  }
                  disabled={decide.isPending}
                >
                  {t('finance.approvals.reject')}
                </Button>
              </div>

              {decide.isError && decide.variables?.id === approval.id ? (
                <p className="text-negative text-[12.5px]">{decide.error.message}</p>
              ) : null}
            </div>
          </Card>
        )
      })}

      {/* Keyed on nothing and gated on the record, like the credential dialog:
          a null record has no title to render, so the sheet simply is not
          there rather than open-with-empty-props. */}
      {confirming ? (
        <Sheet
          open
          onClose={() => setConfirming(null)}
          title={t('finance.approvals.confirmTitle', {
            number: confirming.caseNumber,
          })}
          footer={
            <>
              <Button
                className="w-full"
                disabled={decide.isPending}
                onClick={() =>
                  decide.mutate(
                    {
                      id: confirming.id,
                      caseId: confirming.caseId,
                      decision: 'approve',
                    },
                    { onSuccess: () => setConfirming(null) },
                  )
                }
              >
                {t('finance.approvals.approve')}
              </Button>
              <Button
                className="w-full"
                variant="secondary"
                onClick={() => setConfirming(null)}
              >
                {t('common.actions.cancel')}
              </Button>
            </>
          }
        >
          <p className="text-text-body text-[13px] leading-relaxed">
            {t('finance.approvals.confirmBody', {
              number: confirming.caseNumber,
              current: formatMoney(
                confirming.currentRate.amount,
                confirming.currentRate.currency,
              ),
              requested: formatMoney(
                confirming.requestedRate.amount,
                confirming.requestedRate.currency,
              ),
            })}
          </p>
        </Sheet>
      ) : null}
    </div>
  )
}


export function FinancePage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  /* The date range lives in the URL beside the tab, and for the same reason: a
     filtered view is a place somebody sends a colleague a link to, and a range
     held only in component state makes "receivables over the last year" a thing
     you can look at but not send. `?range=` therefore arrives from outside and
     may say anything — including `1W` from a bookmark taken before the
     vocabulary was reconciled — so it goes through `isDateRange` rather than
     being cast; an unknown value falls back to the default period instead of
     asking the server for one it does not implement. */
  const requestedRange = searchParams.get('range')
  const range: DateRange = isDateRange(requestedRange) ? requestedRange : '3M'

  const requested = searchParams.get('tab')
  const tab: TabKey = TABS.some((t) => t.key === requested)
    ? (requested as TabKey)
    : 'balances'

  /* Which case's paper the Documents tab is showing (Story 5.4, issue #27). In
     the URL beside the tab, and for the same reason: a document belongs to a
     case, so "the documents on MG-19" is a place somebody sends a colleague a
     link to. Unlike the date range it is NOT page-level — the other four tabs
     are cross-case surfaces and a case filter would mean something different on
     each of them.

     `?case=` is also the only way to narrow this tab at all, and that is the
     archive's shape rather than a missing feature: `GET /api/documents` requires
     a source and answers 422 without one, so there is no cross-case list of
     paper to fall back to. The panel argues this at length. */
  const documentsCaseId = searchParams.get('case') ?? ''

  return (
    <>
      <PageHeader
        title={t('finance.title')}
        subtitle={t('finance.subtitle')}
        actions={
          <DateRangeControl
            value={range}
            onChange={(next) => {
              const params = new URLSearchParams(searchParams)
              params.set('range', next)
              // `replace`, like the tab: narrowing the period is refining the
              // view you are already on, and a Back button that walked back
              // through every period somebody tried is one nobody uses.
              setSearchParams(params, { replace: true })
            }}
          />
        }
      />

      <TabNav
        label={t('finance.sectionsLabel')}
        items={TABS}
        value={tab}
        onChange={(key) => {
          const next = new URLSearchParams(searchParams)
          next.set('tab', key)
          setSearchParams(next, { replace: true })
        }}
      />

      {/* Keyed on the tab only, not on the range: changing the date range
          refreshes the numbers inside the panel that is already open, and
          replaying an entrance for that would read as a navigation the user
          did not make. */}
      <TabPanel tabKey={tab}>
        {tab === 'balances' ? <BalancesPanel range={range} /> : null}
        {tab === 'aging' ? <AgingPanel range={range} /> : null}
        {tab === 'payments' ? <PaymentsPanel range={range} /> : null}
        {tab === 'approvals' ? <ApprovalsPanel /> : null}
        {tab === 'documents' ? (
          <DocumentsPanel
            caseId={documentsCaseId}
            onSelectCase={(caseId) => {
              const next = new URLSearchParams(searchParams)
              next.set('case', caseId)
              // `replace`, like the tab: choosing a case is refining the view
              // you are on, and a Back button that walked back through every
              // case somebody tried is a Back button nobody uses.
              setSearchParams(next, { replace: true })
            }}
          />
        ) : null}
      </TabPanel>
    </>
  )
}
